import { useEffect } from "react";
import { Outlet, useLoaderData, useLocation } from "react-router";
import type { SessionUser } from "../lib/session-loader";
import { initI18n, currentLocale } from "../lib/i18n";
import { authedFetch } from "../lib/api-client";
import { Sidebar } from "./shell/Sidebar";
import { TabBar } from "./shell/TabBar";
import { MobileTopBar } from "./shell/MobileTopBar";

/**
 * Pathname prefixes that suppress the shell entirely.
 * Routes on this list render only <Outlet/> — they own their own chrome.
 */
const CHROMELESS_PREFIXES = [
  "/welkom",
  "/spreken/exam",
  "/spreken/report",
  "/schrijven/exam",
  "/schrijven/practice",
  "/schrijven/report",
  "/welkom-bezoeker",
];

function isChromeless(pathname: string): boolean {
  return CHROMELESS_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(prefix + "/"),
  );
}

export function AppLayout() {
  const session = useLoaderData<SessionUser>();
  const { pathname } = useLocation();

  // Reconcile the cookie locale with the user's DB preference on first mount.
  useEffect(() => {
    const desired = session.preferredLanguage;
    if (!desired) {
      // Why: legacy account or fresh signup with no DB value — write the
      // current chrome locale back so subsequent visits skip this step.
      void authedFetch("/user/preferences", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ preferredLanguage: currentLocale() }),
      }).catch(() => undefined);
      return;
    }
    if (desired !== currentLocale()) {
      void initI18n(desired).then(() => {
        document.cookie = `a2easy_lang=${desired}; path=/; max-age=${
          60 * 60 * 24 * 365
        }; SameSite=Lax`;
      });
    }
  }, [session.preferredLanguage]);

  // Chromeless routes (exam runner, report, onboarding, public landing)
  // render no sidebar / tab bar — they own their own minimal chrome.
  if (isChromeless(pathname)) {
    return <Outlet context={session} />;
  }

  return (
    <div className="min-h-dvh bg-[var(--bg)] text-[var(--ink)] flex flex-col lg:flex-row">
      {/* Desktop sidebar — sticky, full-height */}
      <Sidebar displayName={session.displayName ?? ""} />

      {/* Main content column */}
      <div className="flex flex-col flex-1 min-w-0">
        {/* Mobile top bar */}
        <MobileTopBar displayName={session.displayName ?? ""} />

        {/* Page content — wide layout, max ~1280px */}
        <main className="flex-1 w-full max-w-[1280px] mx-auto px-5 sm:px-6 lg:px-10 py-6 sm:py-8">
          <Outlet context={session} />
        </main>

        {/* Mobile bottom tab bar */}
        <TabBar />
      </div>
    </div>
  );
}
